
import React, { useState, useEffect, useRef } from 'react';
import { User, Invoice } from '../types';
import { storageService } from '../services/storageService';
import { generateInvoice } from '../services/pdfService';
import { X, User as UserIcon, HardDrive, Shield, Download, Upload, Trash2, Save, Database, AlertTriangle, Loader2, CreditCard, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SettingsModalProps {
  user: User;
  invoices: Invoice[];
  onClose: () => void;
  onUpdateUser: (user: User) => void;
  onLogout: () => void;
  onDataReset: () => void; // Reload app after import
}

const SettingsModal: React.FC<SettingsModalProps> = ({ user, invoices, onClose, onUpdateUser, onLogout, onDataReset }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'data' | 'security' | 'billing'>('profile');
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [isSaving, setIsSaving] = useState(false);
  const [storageUsage, setStorageUsage] = useState(0);
  
  // Data Import State
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importStatus, setImportStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');

  useEffect(() => {
      setStorageUsage(storageService.getStorageUsage());
  }, []);

  const handleSaveProfile = async () => {
      setIsSaving(true);
      try {
          const updated = await storageService.updateUserProfile(user.id, { name, email });
          onUpdateUser(updated);
          // Simulate brief delay for feedback
          await new Promise(r => setTimeout(r, 500));
      } catch (e) {
          console.error(e);
      } finally {
          setIsSaving(false);
      }
  };

  const handleExport = async () => {
      const json = await storageService.exportUserData(user.id);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dc_propchain_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
      fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setImportStatus('processing');
      const reader = new FileReader();
      reader.onload = async (event) => {
          try {
              const json = event.target?.result as string;
              await storageService.importUserData(user.id, json);
              setImportStatus('success');
              setTimeout(() => {
                  onDataReset(); // Force reload to see new data
              }, 1500);
          } catch (err) {
              console.error(err);
              setImportStatus('error');
          }
      };
      reader.readAsText(file);
  };

  const handleDownloadInvoice = (inv: Invoice) => {
      generateInvoice(inv, user);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={onClose}></div>
        
        <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="w-full max-w-4xl bg-slate-900 rounded-3xl border border-slate-700 shadow-2xl overflow-hidden flex flex-col md:flex-row h-[70vh] md:h-[600px] relative z-10"
        >
            {/* Close Button Mobile */}
            <button onClick={onClose} className="absolute top-4 right-4 md:hidden text-slate-400 z-20">
                <X className="w-6 h-6" />
            </button>

            {/* Sidebar */}
            <div className="w-full md:w-64 bg-slate-950 border-b md:border-b-0 md:border-r border-slate-800 p-6 flex flex-col">
                <h2 className="text-xl font-black text-white mb-8 hidden md:block">Settings</h2>
                
                <nav className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible">
                    <button 
                        onClick={() => setActiveTab('profile')}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'profile' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-900'}`}
                    >
                        <UserIcon className="w-4 h-4" /> Profile
                    </button>
                    <button 
                        onClick={() => setActiveTab('billing')}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'billing' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-900'}`}
                    >
                        <CreditCard className="w-4 h-4" /> Billing & Invoices
                    </button>
                    <button 
                        onClick={() => setActiveTab('data')}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'data' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-900'}`}
                    >
                        <HardDrive className="w-4 h-4" /> Data & Storage
                    </button>
                    <button 
                        onClick={() => setActiveTab('security')}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'security' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-900'}`}
                    >
                        <Shield className="w-4 h-4" /> Account
                    </button>
                </nav>

                <div className="mt-auto pt-6 border-t border-slate-800 hidden md:block">
                    <div className="text-[10px] text-slate-500 font-mono">
                        Build v2.5.0<br/>
                        Session ID: {user.id.substring(0,8)}
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 bg-slate-900 p-8 overflow-y-auto custom-scrollbar relative">
                <button onClick={onClose} className="absolute top-6 right-6 hidden md:block text-slate-500 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                </button>

                <AnimatePresence mode="wait">
                    {activeTab === 'profile' && (
                        <motion.div 
                            key="profile"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-8"
                        >
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-1">Personal Profile</h3>
                                <p className="text-slate-400 text-sm">Manage your identity within the PropChain terminal.</p>
                            </div>

                            <div className="space-y-6 max-w-lg">
                                <div className="flex items-center gap-6">
                                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-3xl font-bold text-white shadow-xl">
                                        {user.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <button className="text-xs bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg border border-slate-700 transition-colors">
                                            Change Avatar
                                        </button>
                                        <p className="text-[10px] text-slate-500 mt-2">Max file size 2MB</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5 ml-1">Display Name</label>
                                        <input 
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5 ml-1">Email Address</label>
                                        <input 
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                                        />
                                    </div>
                                </div>

                                <button 
                                    onClick={handleSaveProfile}
                                    disabled={isSaving}
                                    className="bg-white text-slate-900 px-6 py-3 rounded-xl font-bold hover:bg-emerald-50 transition-colors flex items-center gap-2 disabled:opacity-70"
                                >
                                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Save Changes
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'billing' && (
                        <motion.div
                            key="billing"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-8"
                        >
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-1">Billing & Invoices</h3>
                                <p className="text-slate-400 text-sm">Manage your subscription and download tax invoices.</p>
                            </div>

                            <div className="bg-gradient-to-r from-slate-900 to-slate-800 border border-slate-700 rounded-2xl p-6 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 blur-3xl rounded-full"></div>
                                <div className="relative z-10 flex justify-between items-start">
                                    <div>
                                        <p className="text-xs text-slate-400 font-bold uppercase mb-1">Current Plan</p>
                                        <h4 className="text-3xl font-black text-white mb-2">{user.subscription?.plan || 'Starter'}</h4>
                                        <p className="text-sm text-emerald-400 font-medium">
                                            {user.subscription?.plan === 'Starter' ? 'Upgrade to unlock features' : 'Active Subscription'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-slate-400 font-bold uppercase mb-1">Renewal Date</p>
                                        <p className="text-white font-mono">
                                            {user.subscription?.renewalDate ? new Date(user.subscription.renewalDate).toLocaleDateString() : 'N/A'}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1 capitalize">{user.subscription?.billingPeriod} Billing</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-lg font-bold text-white mb-4">Invoice History</h4>
                                {invoices.length > 0 ? (
                                    <div className="space-y-3">
                                        {invoices.map(inv => (
                                            <div key={inv.id} className="flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-xl hover:border-slate-700 transition-colors">
                                                <div className="flex items-center gap-4">
                                                    <div className="p-2 bg-emerald-500/10 text-emerald-500 rounded-lg">
                                                        <FileText className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-white">Invoice #{inv.id.substring(0,8).toUpperCase()}</p>
                                                        <p className="text-xs text-slate-500">{new Date(inv.date).toLocaleDateString()} • {inv.plan} Plan</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="text-white font-mono font-bold">€{inv.amount}</span>
                                                    <button 
                                                        onClick={() => handleDownloadInvoice(inv)}
                                                        className="text-xs text-emerald-400 hover:text-emerald-300 font-bold flex items-center gap-1"
                                                    >
                                                        <Download className="w-3 h-3" /> PDF
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-slate-500 border border-dashed border-slate-800 rounded-xl">
                                        No invoices found.
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'data' && (
                        <motion.div 
                            key="data"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-8"
                        >
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-1">Data Management</h3>
                                <p className="text-slate-400 text-sm">Control your local storage and portability.</p>
                            </div>

                            {/* Storage Meter */}
                            <div className="bg-slate-950 border border-slate-800 p-6 rounded-2xl">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="font-bold text-white flex items-center gap-2">
                                        <Database className="w-4 h-4 text-emerald-500" /> Local Storage Usage
                                    </h4>
                                    <span className="text-xs font-mono text-emerald-400">{storageUsage} KB used</span>
                                </div>
                                <div className="w-full bg-slate-800 h-3 rounded-full overflow-hidden mb-2">
                                    <div 
                                        className="h-full bg-gradient-to-r from-emerald-600 to-teal-400 transition-all duration-500"
                                        style={{ width: `${Math.min((storageUsage / 5000) * 100, 100)}%` }} // Assuming 5MB limit roughly
                                    ></div>
                                </div>
                                <p className="text-[10px] text-slate-500">
                                    Your data is stored locally on this device. Total capacity is approximately 5MB.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Export */}
                                <div className="bg-slate-950 border border-slate-800 p-6 rounded-2xl flex flex-col items-start hover:border-emerald-500/30 transition-colors group">
                                    <div className="p-3 bg-slate-900 rounded-xl mb-4 group-hover:bg-emerald-500/10 transition-colors">
                                        <Download className="w-6 h-6 text-emerald-500" />
                                    </div>
                                    <h4 className="font-bold text-white mb-2">Export Backup</h4>
                                    <p className="text-xs text-slate-400 mb-6 flex-1">
                                        Download a secure JSON file containing all your properties, tasks, and settings.
                                    </p>
                                    <button 
                                        onClick={handleExport}
                                        className="w-full py-2.5 rounded-lg border border-slate-700 text-slate-300 text-sm font-bold hover:bg-slate-800 hover:text-white transition-colors"
                                    >
                                        Download JSON
                                    </button>
                                </div>

                                {/* Import */}
                                <div className="bg-slate-950 border border-slate-800 p-6 rounded-2xl flex flex-col items-start hover:border-blue-500/30 transition-colors group">
                                    <div className="p-3 bg-slate-900 rounded-xl mb-4 group-hover:bg-blue-500/10 transition-colors">
                                        <Upload className="w-6 h-6 text-blue-500" />
                                    </div>
                                    <h4 className="font-bold text-white mb-2">Import Data</h4>
                                    <p className="text-xs text-slate-400 mb-6 flex-1">
                                        Restore your data from a previous backup file. This will overwrite current data.
                                    </p>
                                    
                                    <input 
                                        type="file" 
                                        ref={fileInputRef}
                                        className="hidden" 
                                        accept=".json"
                                        onChange={handleFileChange}
                                    />

                                    <button 
                                        onClick={handleImportClick}
                                        disabled={importStatus === 'processing'}
                                        className="w-full py-2.5 rounded-lg border border-slate-700 text-slate-300 text-sm font-bold hover:bg-slate-800 hover:text-white transition-colors flex justify-center items-center gap-2"
                                    >
                                        {importStatus === 'processing' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Select File'}
                                    </button>
                                    
                                    {importStatus === 'success' && <span className="text-[10px] text-emerald-500 mt-2 block w-full text-center">Import Successful! Reloading...</span>}
                                    {importStatus === 'error' && <span className="text-[10px] text-red-500 mt-2 block w-full text-center">Invalid File Format.</span>}
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'security' && (
                        <motion.div 
                            key="security"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-8"
                        >
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-1">Account Security</h3>
                                <p className="text-slate-400 text-sm">Manage access and session details.</p>
                            </div>

                            <div className="bg-red-500/5 border border-red-500/20 p-6 rounded-2xl">
                                <div className="flex items-start gap-4">
                                    <div className="p-2 bg-red-500/10 rounded-lg text-red-500">
                                        <AlertTriangle className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-white mb-1">Danger Zone</h4>
                                        <p className="text-xs text-slate-400 mb-4">
                                            Actions here are irreversible. Please proceed with caution.
                                        </p>
                                        <div className="flex gap-4">
                                            <button 
                                                onClick={onLogout}
                                                className="px-4 py-2 bg-slate-900 border border-slate-700 text-slate-300 rounded-lg text-sm font-bold hover:text-white hover:bg-slate-800 transition-colors"
                                            >
                                                Log Out
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    if(window.confirm("Are you sure? This will wipe all local data.")) {
                                                        localStorage.clear();
                                                        onDataReset();
                                                    }
                                                }}
                                                className="px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm font-bold hover:bg-red-500 hover:text-white transition-colors flex items-center gap-2"
                                            >
                                                <Trash2 className="w-4 h-4" /> Wipe Data
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    </div>
  );
};

export default SettingsModal;
